import {
	getJpegSizeEstimationDetails,
	type JpegSizeEstimationParameters,
} from "@/core/utils/encoded-size-check"
import { isJpegFile } from "@/utils/file"

export const JPEG_SIZE_PREVIEW_LONG_SIDE = 192
const JPEG_SIZE_SAMPLE_COUNT = 4

export const JPEG_SIZE_HYBRID_BASE_PARAMETERS: JpegSizeEstimationParameters = {
	mcuSize: 16,
	fixedOverheadBytes: 32_768,
	quality90BytesPerPixelAtIso100: 0.133,
	quality90QuantizationScale: 20,
	qualityScaleExponent: 0.275,
	isoReference: 100,
	isoExponent: 0.055,
	minimumCalibrationIso: 10,
	maximumCalibrationIso: 20_000,
	missingIsoMultiplier: 1,
	minBytesPerPixel: 0.06,
	maxEstimatedBytesPerPixel: 1.2,
}

const FEATURE_MEANS = [
	1.2895971501525814, 0.10416666666666671, 0.36407153028782785, 0.23877590189899256,
	0.8638001827177803, -3.24154542176673, -3.718957399417839, 0.5039468876859878,
	0.03396436321066151, 0.22222222222222227, -0.28600064334000086,
] as const

const FEATURE_SCALES = [
	1.1637625519376542, 0.30547663122114954, 0.14484764030867361, 0.04978077558066914,
	0.11056890303350539, 0.4308820195002503, 0.48875175373711927, 0.16764430797794252,
	0.01372748068995422, 0.4157397096415489, 0.627819108115608,
] as const

const QUALITY_MODELS = {
	50: [
		-0.4785693853001461, 0.10447678616911434, -0.01987220590727557, 0.06279484104518263,
		-0.04584761222907617, 0.06684347024333866, 0.030871016484779773, 0.06571341586833354,
		-0.08206994574654009, -0.022378673878837824, 0.036408738608206676, 0.007669838391665117,
	],
	60: [
		-0.3545535487813882, 0.10743604405304251, -0.033892205497649354, 0.06404599955795241,
		-0.04804898268166637, 0.0803676476332176, 0.025743538020692156, 0.05950826783884923,
		-0.08122529150337834, -0.020900356708239788, 0.040678635603526005, 0.004491765176215778,
	],
	70: [
		-0.2014749736205434, 0.10567896633149586, -0.03682238118099206, 0.06799207617447525,
		-0.0507312563741647, 0.09039225588615632, 0.020936038158608703, 0.05139742854411492,
		-0.07989413665775982, -0.018734149317807255, 0.03535624076600312, 0.018270084050631567,
	],
	80: [
		-0.0023240479460490846, 0.10396503993141681, -0.06778590753826094, 0.06178278758914167,
		-0.054227089132412476, 0.10656119942674085, 0.01864493228091014, 0.04133863188341758,
		-0.0785177732373394, -0.013474785029374171, 0.05042020709266734, 0.005581854254436393,
	],
	90: [
		0.2949194838548328, 0.09327118709079676, -0.06902372117248312, 0.06035127728862754,
		-0.05891778524065, 0.11032187052006961, 0.016034745671174223, 0.03150299015955182,
		-0.07422459719855339, -0.0061191771812646855, 0.03941276992157299, 0.050457091806234186,
	],
	100: [
		0.6970501920439127, 0.053549188445438684, -0.04143803659306673, 0.036011962970852934,
		-0.04915461245719965, 0.07956623937288006, 0.008278020114423797, 0.014538230263601418,
		-0.04761487099193635, 0.005567757400890789, -0.008393339706107339, 0.11961858570768576,
	],
} as const

export const JPEG_SIZE_HYBRID_PARAMETERS = {
	model: "preview-content-ridge",
	version: 2,
	modelSha256: "9a1fb1cd9c6e3d64f5958a158c8aec3b61fcac2b29ee082f1d28f89383db2469",
	trainingFileCountPerQuality: 132,
	qualities: [50, 60, 70, 80, 90, 100],
	sampleCount: JPEG_SIZE_SAMPLE_COUNT,
	previewLongSide: JPEG_SIZE_PREVIEW_LONG_SIDE,
} as const

export interface JpegPreviewFeatures {
	width: number
	height: number
	pixelCount: number
	lumaMean: number
	lumaStandardDeviation: number
	lumaEntropy: number
	edgeEnergy: number
	highFrequencyEnergy: number
	flatPixelFraction: number
	chromaStandardDeviation: number
}

export interface JpegHybridEstimateItem {
	id: string
	fileName: string
	baseEstimatedBytes: number
	iso?: number | null
	sourceFileBytes?: number
	sourcePixelCount?: number
}

export function estimateJpegHybridBaseBytes(input: {
	width: number
	height: number
	quality: number
	iso?: number | null
}) {
	return getJpegHybridBaseEstimationDetails(input).estimatedBytes
}

export function getJpegHybridBaseEstimationDetails(input: {
	width: number
	height: number
	quality: number
	iso?: number | null
}) {
	return getJpegSizeEstimationDetails(input, JPEG_SIZE_HYBRID_BASE_PARAMETERS)
}

export function getJpegHybridCorrectionMultiplier(input: {
	quality: number
	iso?: number | null
	fileName: string
	sourceFileBytes?: number
	sourcePixelCount?: number
	previewFeatures: JpegPreviewFeatures
}) {
	const qualityPercent = Math.round(Math.min(1, Math.max(0.01, input.quality)) * 100)
	if (!isQualityModel(qualityPercent)) {
		throw new Error(`Unsupported JPEG estimation quality: ${qualityPercent}`)
	}
	const coefficients = QUALITY_MODELS[qualityPercent]

	const usableIso =
		typeof input.iso === "number" && Number.isFinite(input.iso) && input.iso > 0
			? Math.min(20_000, Math.max(10, input.iso))
			: null
	const iso = usableIso ?? 100
	const jpegSource = isJpegFile(input.fileName)
	const sourceBytesPerPixel =
		typeof input.sourceFileBytes === "number" &&
		Number.isFinite(input.sourceFileBytes) &&
		input.sourceFileBytes > 0 &&
		typeof input.sourcePixelCount === "number" &&
		Number.isFinite(input.sourcePixelCount) &&
		input.sourcePixelCount > 0
			? input.sourceFileBytes / input.sourcePixelCount
			: 0.001
	const preview = input.previewFeatures
	const values = [
		Math.log(iso / 100),
		usableIso === null ? 1 : 0,
		preview.lumaMean,
		preview.lumaStandardDeviation,
		preview.lumaEntropy,
		Math.log(preview.edgeEnergy + 0.0001),
		Math.log(preview.highFrequencyEnergy + 0.0001),
		preview.flatPixelFraction,
		preview.chromaStandardDeviation,
		jpegSource ? 1 : 0,
		jpegSource ? Math.log(Math.max(0.001, sourceBytesPerPixel)) : 0,
	]
	let correction = coefficients[0]

	for (let index = 0; index < values.length; index++) {
		correction +=
			(coefficients[index + 1] ?? 0) *
			(((values[index] ?? 0) - (FEATURE_MEANS[index] ?? 0)) / (FEATURE_SCALES[index] ?? 1))
	}

	return Math.exp(correction)
}

export async function estimateJpegHybridGroupBytes<T extends JpegHybridEstimateItem>(
	items: T[],
	quality: number,
	getPreviewFeatures: (item: T) => Promise<JpegPreviewFeatures>,
) {
	if (items.length === 0) return 0

	let estimatedBytes = 0
	for (const strip of splitIntoStrips(items, JPEG_SIZE_SAMPLE_COUNT)) {
		const sample = strip[Math.floor((strip.length - 1) / 2)]
		if (!sample) continue
		const correctionMultiplier = getJpegHybridCorrectionMultiplier({
			...sample,
			quality,
			previewFeatures: await getPreviewFeatures(sample),
		})
		estimatedBytes +=
			strip.reduce((total, item) => total + item.baseEstimatedBytes, 0) * correctionMultiplier
	}

	return Math.max(1, Math.round(estimatedBytes))
}

export async function extractJpegPreviewFeatures(preview: Blob): Promise<JpegPreviewFeatures> {
	const bitmap = await createImageBitmap(preview)
	try {
		const scale = Math.min(
			1,
			JPEG_SIZE_PREVIEW_LONG_SIDE / Math.max(1, bitmap.width, bitmap.height),
		)
		const width = Math.max(1, Math.round(bitmap.width * scale))
		const height = Math.max(1, Math.round(bitmap.height * scale))
		const canvas = document.createElement("canvas")
		canvas.width = width
		canvas.height = height
		const context = canvas.getContext("2d", { alpha: false, willReadFrequently: true })
		if (!context) throw new Error("2D canvas unavailable for JPEG size estimation")
		context.drawImage(bitmap, 0, 0, width, height)
		const rgba = context.getImageData(0, 0, width, height).data
		const count = width * height
		const luma = new Float32Array(count)
		const histogram = new Uint32Array(32)
		let lumaSum = 0
		let lumaSquareSum = 0
		let chromaBlueSum = 0
		let chromaBlueSquareSum = 0
		let chromaRedSum = 0
		let chromaRedSquareSum = 0

		for (let index = 0; index < count; index++) {
			const offset = index * 4
			const red = rgba[offset] ?? 0
			const green = rgba[offset + 1] ?? 0
			const blue = rgba[offset + 2] ?? 0
			const value = (0.299 * red + 0.587 * green + 0.114 * blue) / 255
			const chromaBlue = (-0.168736 * red - 0.331264 * green + 0.5 * blue) / 255
			const chromaRed = (0.5 * red - 0.418688 * green - 0.081312 * blue) / 255
			luma[index] = value
			lumaSum += value
			lumaSquareSum += value * value
			chromaBlueSum += chromaBlue
			chromaBlueSquareSum += chromaBlue * chromaBlue
			chromaRedSum += chromaRed
			chromaRedSquareSum += chromaRed * chromaRed
			const histogramIndex = Math.min(31, Math.floor(value * 32))
			histogram[histogramIndex] = (histogram[histogramIndex] ?? 0) + 1
		}

		let edgeSum = 0
		let edgeComparisons = 0
		let highFrequencySum = 0
		let highFrequencyCount = 0
		let flatCount = 0
		let flatCandidateCount = 0
		for (let y = 0; y < height; y++) {
			for (let x = 0; x < width; x++) {
				const index = y * width + x
				const value = luma[index] ?? 0
				let localEdgeSum = 0
				let localEdgeCount = 0
				if (x > 0) {
					const difference = Math.abs(value - (luma[index - 1] ?? 0))
					edgeSum += difference
					edgeComparisons++
					localEdgeSum += difference
					localEdgeCount++
				}
				if (y > 0) {
					const difference = Math.abs(value - (luma[index - width] ?? 0))
					edgeSum += difference
					edgeComparisons++
					localEdgeSum += difference
					localEdgeCount++
				}
				if (localEdgeCount === 2) {
					flatCandidateCount++
					if (localEdgeSum / localEdgeCount < 0.02) flatCount++
				}
				if (x > 0 && x < width - 1 && y > 0 && y < height - 1) {
					const laplacian = Math.abs(
						4 * value -
							(luma[index - 1] ?? 0) -
							(luma[index + 1] ?? 0) -
							(luma[index - width] ?? 0) -
							(luma[index + width] ?? 0),
					)
					highFrequencySum += laplacian / 4
					highFrequencyCount++
				}
			}
		}

		let entropy = 0
		for (const binCount of histogram) {
			if (binCount === 0) continue
			const probability = binCount / count
			entropy -= probability * Math.log2(probability)
		}
		const lumaMean = lumaSum / count
		const chromaBlueMean = chromaBlueSum / count
		const chromaRedMean = chromaRedSum / count

		return {
			width,
			height,
			pixelCount: count,
			lumaMean,
			lumaStandardDeviation: Math.sqrt(
				Math.max(0, lumaSquareSum / count - lumaMean * lumaMean),
			),
			lumaEntropy: entropy / Math.log2(histogram.length),
			edgeEnergy: edgeComparisons > 0 ? edgeSum / edgeComparisons : 0,
			highFrequencyEnergy: highFrequencyCount > 0 ? highFrequencySum / highFrequencyCount : 0,
			flatPixelFraction: flatCandidateCount > 0 ? flatCount / flatCandidateCount : 0,
			chromaStandardDeviation: Math.sqrt(
				Math.max(
					0,
					(chromaBlueSquareSum / count -
						chromaBlueMean * chromaBlueMean +
						chromaRedSquareSum / count -
						chromaRedMean * chromaRedMean) /
						2,
				),
			),
		}
	} finally {
		bitmap.close()
	}
}

function splitIntoStrips<T>(items: T[], maximumStrips: number) {
	const stripCount = Math.min(maximumStrips, items.length)
	return Array.from({ length: stripCount }, (_, index) => {
		const start = Math.floor((index * items.length) / stripCount)
		const end = Math.floor(((index + 1) * items.length) / stripCount)
		return items.slice(start, end)
	})
}

function isQualityModel(value: number): value is keyof typeof QUALITY_MODELS {
	return value in QUALITY_MODELS
}
